import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {fetchTranslations} from "../types/translationsClients";
import DocklineFX from "../components/DocklineFX";
import HeroFX from "../components/HeroFX";
import {Helmet} from 'react-helmet-async';

const FotografMuenchen = () => {
    const [dict, setDict] = useState<Record<string, string> | null>(null);
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Landing hero parallax + tilt on scroll with visibility toggle
    useEffect(() => {
        const hero = document.querySelector('.landing-hero-cover') as HTMLElement | null;
        if (!hero) return;

        const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

        let rafId = 0;
        const loop = () => {
            const doc = document.documentElement;
            const st = doc.scrollTop || document.body.scrollTop || 0;

            const maxScroll = Math.max(1, doc.scrollHeight - doc.clientHeight);
            const hideThreshold = 0.5 * maxScroll;

            if (st >= hideThreshold) {
                hero.classList.add('lh-hidden');
            } else {
                hero.classList.remove('lh-hidden');

                const vh = window.innerHeight || 1;
                const t = clamp(st / (vh * 1.4));

                const scale = 1 - 0.20 * t;
                const tilt = 6 * t;

                hero.style.transform = `translateX(-50%) perspective(1000px) scale(${scale}) rotateX(${tilt}deg)`;
            }

            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
        return () => {
            hero.classList.remove('lh-hidden');
            cancelAnimationFrame(rafId);
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Fotograf in München | Dockline Studios</title>
                <meta
                    name="description"
                    content="Ihr Fotograf in München: Dockline Studios bietet Business- und Eventfotografie, Business-Headshots, Teamfotos, Imagefilme und Videoproduktion für Unternehmen in und um München."
                />
                <meta
                    name="keywords"
                    content="Fotograf München, Business Fotograf München, Eventfotograf München, Unternehmensfotografie München, Imagefilm München, Videoproduktion München, Dockline Studios"
                />
                <link rel="canonical" href="https://dockline.digital/fotograf-muenchen"/>

                {/* Open Graph */}
                <meta property="og:type" content="website"/>
                <meta property="og:title" content="Fotograf in München – Dockline Studios"/>
                <meta
                    property="og:description"
                    content="Business-Fotografie, Eventfoto, Headshots und Videoproduktion in München. Dockline Studios setzt Ihr Unternehmen professionell in Szene."
                />
                <meta property="og:url" content="https://dockline.digital/fotograf-muenchen"/>
                <meta property="og:image" content="https://dockline.digital/pics/og/studios-og.jpg"/>
                <meta property="og:locale" content="de_DE"/>

                {/* JSON-LD schema for local photography / video service */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "LocalBusiness",
                  "name": "Dockline Studios – Fotograf in München",
                  "url": "https://dockline.digital/fotograf-muenchen",
                  "logo": "https://dockline.digital/pics/logo/sec/dstudios_logo_sec.svg",
                  "image": "https://dockline.digital/pics/og/studios-og.jpg",
                  "description": "Dockline Studios ist Ihr Fotograf in München für Business-Headshots, Eventfotografie, Imagefilme und Social-Media-Content.",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Musterstraße 1",
                    "postalCode": "80331",
                    "addressLocality": "München",
                    "addressCountry": "DE"
                  },
                  "areaServed": {
                    "@type": "City",
                    "name": "München",
                    "addressCountry": "DE"
                  },
                  "priceRange": "€€",
                  "makesOffer": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Business-Headshots in München",
                        "serviceType": "Business-Headshots"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Eventfotografie in München",
                        "serviceType": "Eventfotografie"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Imagefilm & Social Clips",
                        "serviceType": "Imagefilm & Videoproduktion"
                      }
                    }
                  ]
                }
                `}</script>

                {/* JSON-LD FAQ schema */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "Welche Arten von Projekten übernimmt Dockline Studios?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dockline Studios übernimmt Business-Fotografie, Eventreportagen, Business-Headshots und Teamfotos, kurze Marketing-Clips, Imagefilme und Liveproduktion. Wir erstellen moderne, klare Bildsprache, die zu Ihrem Markenauftritt passt und auf Website, Social Media und in Präsentationen funktioniert."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Arbeiten Sie nur als Fotograf in München oder auch darüber hinaus?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Unser Produktionsstandort ist München, wir arbeiten aber auch mit Kundinnen und Kunden aus dem Umland und anderen Regionen zusammen. Viele Shootings finden in und um München statt, während Abstimmung, Auswahl und finale Lieferung vollständig remote funktionieren."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Was kostet ein Fotograf oder eine Videoproduktion in München?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Die Kosten hängen von Art und Umfang des Projekts ab – zum Beispiel von Vorbereitung, Dauer des Shootings, Anzahl der Motive und dem benötigten Schnitt. Üblicherweise kalkulieren wir ein klares Projektangebot oder Tagessätze auf Basis Ihres Briefings und schicken Ihnen ein transparentes Angebot mit Leistungsumfang und Zeitplan."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Bieten Sie auch Business-Headshots und Teamfotos vor Ort im Unternehmen an?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja. Für Business-Headshots und Teamfotos kommen wir direkt zu Ihnen ins Unternehmen in München oder Umgebung. Wir bringen das nötige Equipment mit und sorgen für Licht, Hintergrund und ein Setup, das zu Ihrer Unternehmenskultur und Ihrem Auftritt passt."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Produzieren Sie auch Imagefilme, Recruiting-Videos oder Social-Media-Clips?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Neben klassischer Fotografie produzieren wir auch kurze Videos für Recruiting, Social Media, Produktvorstellungen und Imagekommunikation. Wir unterstützen bei Konzept, Drehplanung, Dreh und Schnitt, damit das Ergebnis zu Ihren Kanälen und Zielgruppen passt."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Können Sie mit vorhandenem Foto- oder Videomaterial arbeiten?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja. Häufig ist es sinnvoll, bestehendes Material neu zu schneiden oder zu aktualisieren. Wir können aus vorhandenem Bild- und Videomaterial neue Schnitte erstellen, Formate für Social Media anpassen oder veraltete Teamfotos durch einheitliche, aktuelle Motive ersetzen."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Wie früh sollten wir ein Shooting oder eine Videoproduktion buchen?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Für Events und aufwendigere Produktionen ist es ideal, so früh wie möglich anzufragen, damit Termin und Location sicher eingeplant werden können. Kleinere Sessions wie Business-Headshots oder kurze Content-Shootings lassen sich je nach Auslastung auch kurzfristiger realisieren."
                      }
                    }
                  ]
                }
                `}</script>
            </Helmet>

            <div id="studios-page" style={{'--fx-color': '#FF5A52', '--fx-glow': 'rgba(255,90,82,0.32)'} as React.CSSProperties}>
                <div className="landing-hero-cover hero-anim">
                    <img src="/pics/landingPref4Studios.webp" alt="" className="landing-hero" id="landing-hero"/>
                    <HeroFX/>
                    <div className="landing-hero-centered-text">
                        <h1 className="studio-color">Fotograf in München</h1>
                        <h2>Dockline Studios – Foto &amp; Videoproduktion für Unternehmen</h2>
                    </div>
                    <a
                        className="landing-hero-bottom-hint"
                        onClick={() => window.scrollTo({top: window.innerHeight, behavior: 'smooth'})}
                        style={{cursor: 'pointer'}}
                    >
                        Scroll to Explore
                    </a>
                </div>

                <div className="content">
                    <DocklineFX color="#FF5A52"/>

                    {/* Intro tailored to Munich */}
                    <section className="studios-intro">
                        <div className="section-intro">
                            <h2 className="studio-color">Business-Fotografie für München und Umgebung</h2>
                            <p>
                                Dockline Studios ist Ihr <strong>Fotograf in München</strong> für Unternehmen, Events
                                und
                                Personal Branding. Als <strong>Business Fotograf in München</strong> erstellen wir
                                <strong> Business-Headshots</strong>, <strong>Teamfotos für Ihre Website</strong>,
                                Eventreportagen und kurze <strong>Social Media Videos</strong>, die auf Website,
                                LinkedIn
                                und in Präsentationen funktionieren.
                            </p>
                        </div>
                    </section>

                    {/* Services (same layout, localized copy) */}
                    <section className="studios-services" aria-labelledby="studios-services-title">
                        <div className="section-intro">
                            <h2 id="studios-services-title">Leistungen als Fotograf in München</h2>
                        </div>
                        <div className="studios-services__cards">
                            <article className="studios-card">
                                <i className="bi bi-camera-video"></i>
                                <h3>Eventfoto &amp; Video</h3>
                                <p>
                                    Konferenzen, Team-Events, Kundentermine: Als <strong>Eventfotograf in
                                    München </strong>
                                    begleiten wir Ihre Veranstaltung mit einem klaren, dokumentarischen Stil – ideal als
                                    Bildstrecke für Website, Presse, Social Media und interne Kommunikation.
                                </p>
                            </article>
                            <article className="studios-card">
                                <i className="bi bi-person-badge"></i>
                                <h3>Business-Headshots &amp; Teamfotos</h3>
                                <p>
                                    Einheitliche Portraits und <strong>Business-Headshots in München</strong> für
                                    Website,
                                    LinkedIn und Pitch-Decks – inklusive <strong>Headshots für LinkedIn</strong> und
                                    <strong> Teamfotos für Ihre Website</strong>. Wir sorgen für Licht, Hintergrund und
                                    Anleitung, damit Menschen natürlich und professionell wirken.
                                </p>
                            </article>
                            <article className="studios-card">
                                <i className="bi bi-film"></i>
                                <h3>Imagefilme &amp; Social Clips</h3>
                                <p>
                                    Kurze, prägnante Videos für Recruiting, Produktvorstellungen oder Social Media – von
                                    <strong> Imagefilmen für Unternehmen in München</strong> über
                                    <strong> Recruiting Videos</strong> bis hin zu <strong>Social Media Videos</strong>,
                                    die speziell für Ihre Kanäle produziert werden.
                                </p>
                            </article>
                            <article className="studios-card">
                                <i className="bi bi-broadcast"></i>
                                <h3>Liveproduktion &amp; Streaming</h3>
                                <p>
                                    Mehrkamera-Setup, sauberer Ton, Einblendungen und Aufzeichnung – Ihr
                                    <strong> Livestream Service in München</strong> für digitale Events, Webinare oder
                                    hybride Formate. Auf Wunsch mit nachträglichem Schnitt zu Highlight-Clips.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* Reference Work (same images, localized text) */}
                    <section className="studios-references" aria-labelledby="studios-references-title">
                        <div className="section-intro">
                            <h2 id="studios-references-title">Beispiele aus unserer Arbeit</h2>
                            <p>
                                Ein Einblick in die Art von Bildsprache, die wir als <strong>Business Fotograf in
                                München</strong>
                                für Kundinnen und Kunden entwickeln – von <strong>Business-Headshots</strong> über
                                <strong> Eventfotografie</strong> bis hin zu <strong>Video-Content</strong>.
                            </p>
                        </div>

                        <div className="studios-overview__grid">
                            <figure className="media-frame">
                                <img src="/pics/subway.webp" alt="Architektur- und Stadtfotografie"/>
                                <figcaption>Architektur- und Stadtmotive – modern, klar, grafisch für Markenauftritte
                                </figcaption>
                            </figure>
                            <figure className="media-frame">
                                <img src="/pics/headshots2.jpeg.webp" alt="Business-Headshots"/>
                                <figcaption>Business-Headshots in München – Portraits für Teams und Führungskräfte
                                </figcaption>
                            </figure>
                            <figure className="media-frame">
                                <img src="/pics/event3.jpeg.webp" alt="Eventfotografie"/>
                                <figcaption>Eventfotografie in München – Konferenzen, Kundenevents und
                                    Firmenveranstaltungen
                                </figcaption>
                            </figure>
                            <figure className="media-frame">
                                <img src="/pics/livestream.jpeg.webp" alt="Liveproduktion & Streaming"/>
                                <figcaption>Liveproduktion &amp; Streaming – Videoproduktion aus München für digitale
                                    Events
                                </figcaption>
                            </figure>
                        </div>
                    </section>

                    {/* Tools / editing workflow */}
                    <section className="studios-tools" aria-labelledby="studios-tools-title">
                        <div className="section-intro">
                            <h2 id="studios-tools-title">Bearbeitung &amp; Postproduktion</h2>
                            <p>
                                Von der Auswahl über den Schnitt bis zum finalen Look – wir liefern konsistente, saubere
                                Ergebnisse in den Formaten, die Sie brauchen. Ideal, wenn bestehendes Material neuen
                                Schnitt benötigt oder wenn Sie <strong>schlechte Teamfotos ersetzen</strong> möchten.
                            </p>
                        </div>
                        <div className="studios-tools__grid">
                            <figure className="media-frame">
                                <img src="/pics/davinci.webp" alt="Video-Postproduktion in DaVinci Resolve"/>
                                <figcaption>Video-Postproduktion in DaVinci Resolve</figcaption>
                            </figure>
                            <figure className="media-frame">
                                <img src="/pics/affinity.webp" alt="Foto-Retusche in Affinity Photo"/>
                                <figcaption>Foto-Retusche und Compositing in Affinity Photo</figcaption>
                            </figure>
                        </div>
                    </section>

                    {/* === Why Dockline Studios – Fotograf in München === */}
                    <section className="systems-why" aria-labelledby="fotograf-why-title">
                        <div className="section-intro">
                            <h2 id="fotograf-why-title" className="studio-color">
                                Warum Dockline Studios als Fotograf in München?
                            </h2>
                            <p>
                                Dockline Studios verbindet klare, moderne Bildsprache mit einer persönlichen, unkomplizierten
                                Zusammenarbeit. Sie arbeiten direkt mit dem Fotografen und Videografen zusammen – vom ersten
                                Konzept bis zur Übergabe der finalen Dateien.
                            </p>
                        </div>
                        <div className="systems-why__grid">
                            <article className="systems-card systems-why__item">
                                <h3>Direkte Zusammenarbeit statt große Crew</h3>
                                <p>
                                    Sie haben einen direkten Ansprechpartner, der zugleich hinter der Kamera steht. Das macht
                                    Absprachen einfach, hält das Set klein und sorgt dafür, dass Ihre Wünsche ohne Umwege im
                                    Bild landen – ob Headshots, Events oder Social-Media-Content.
                                </p>
                            </article>
                            <article className="systems-card systems-why__item">
                                <h3>Business-Fokus für Web, LinkedIn &amp; Social Media</h3>
                                <p>
                                    Die Ergebnisse sind von Anfang an für reale Einsatzzwecke gedacht: Website, LinkedIn,
                                    Recruiting-Kampagnen, Präsentationen oder Social Media. Bildstil, Formate und Schnitte
                                    werden so gewählt, dass sie zu Ihrem Unternehmen in München passen.
                                </p>
                            </article>
                            <article className="systems-card systems-why__item">
                                <h3>Schlanke Produktion, transparente Preise</h3>
                                <p>
                                    Als kleine, flexible Einheit arbeitet Dockline Studios ohne aufwendige Agenturstrukturen.
                                    Dadurch erhalten Sie professionelle Foto- und Videoproduktion mit
                                    <em> klaren, nachvollziehbaren Projektpreisen</em> – ideal für Unternehmen, Events und
                                    Teams, die hochwertige Inhalte ohne überzogene Budgets suchen.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* === FAQ === */}
                    <section className="systems-faq" aria-labelledby="studios-faq-title">
                        <div className="section-intro">
                            <h2 id="studios-faq-title" className="studio-color">Häufige Fragen zu
                                Foto- &amp; Videoproduktion</h2>
                            <p>
                                Antworten auf typische Fragen rund um Business-Fotografie, Eventbegleitung und
                                Videoproduktion
                                in München. Wenn Ihre Situation hier nicht vorkommt, schreiben Sie uns kurz – wir melden
                                uns
                                mit einer Einschätzung.
                            </p>
                        </div>
                        <div className="faq-list">
                            <details className="faq-item">
                                <summary><p className="boldText">Welche Arten von Projekten übernimmt Dockline
                                    Studios?</p></summary>
                                <p>
                                    Dockline Studios übernimmt Business-Fotografie, Eventreportagen, Business-Headshots
                                    und
                                    Teamfotos, kurze Marketing-Clips, Imagefilme und Liveproduktion. Wir erstellen
                                    moderne,
                                    klare Bildsprache, die zu Ihrem Markenauftritt passt und auf Website, Social Media
                                    und in
                                    Präsentationen funktioniert.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Arbeiten Sie nur als Fotograf in München oder auch
                                    darüber hinaus?</p></summary>
                                <p>
                                    Unser Produktionsstandort ist München, wir arbeiten aber auch mit Kundinnen und
                                    Kunden aus
                                    dem Umland und anderen Regionen zusammen. Viele Shootings finden in und um München
                                    statt,
                                    während Abstimmung, Auswahl und finale Lieferung vollständig remote funktionieren.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Was kostet ein Fotograf oder eine Videoproduktion in
                                    München?</p></summary>
                                <p>
                                    Die Kosten hängen von Art und Umfang des Projekts ab – zum Beispiel von
                                    Vorbereitung,
                                    Dauer des Shootings, Anzahl der Motive und dem benötigten Schnitt. Üblicherweise
                                    kalkulieren wir ein klares Projektangebot oder Tagessätze auf Basis Ihres Briefings
                                    und
                                    schicken Ihnen ein transparentes Angebot mit Leistungsumfang und Zeitplan.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Bieten Sie auch Business-Headshots und Teamfotos vor
                                    Ort im Unternehmen an?</p></summary>
                                <p>
                                    Ja. Für Business-Headshots und Teamfotos kommen wir direkt zu Ihnen ins Unternehmen
                                    in
                                    München oder Umgebung. Wir bringen das nötige Equipment mit und sorgen für Licht,
                                    Hintergrund und ein Setup, das zu Ihrer Unternehmenskultur und Ihrem Auftritt passt.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Produzieren Sie auch Imagefilme, Recruiting-Videos oder
                                    Social-Media-Clips?</p></summary>
                                <p>
                                    Neben klassischer Fotografie produzieren wir auch kurze Videos für Recruiting,
                                    Social
                                    Media, Produktvorstellungen und Imagekommunikation. Wir unterstützen bei Konzept,
                                    Drehplanung, Dreh und Schnitt, damit das Ergebnis zu Ihren Kanälen und Zielgruppen
                                    passt.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Können Sie mit vorhandenem Foto- oder Videomaterial
                                    arbeiten?</p></summary>
                                <p>
                                    Ja. Häufig ist es sinnvoll, bestehendes Material neu zu schneiden oder zu
                                    aktualisieren.
                                    Wir können aus vorhandenem Bild- und Videomaterial neue Schnitte erstellen, Formate
                                    für
                                    Social Media anpassen oder veraltete Teamfotos durch einheitliche, aktuelle Motive
                                    ersetzen.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Wie früh sollten wir ein Shooting oder eine
                                    Videoproduktion buchen?</p></summary>
                                <p>
                                    Für Events und aufwendigere Produktionen ist es ideal, so früh wie möglich
                                    anzufragen,
                                    damit Termin und Location sicher eingeplant werden können. Kleinere Sessions wie
                                    Business-Headshots oder kurze Content-Shootings lassen sich je nach Auslastung auch
                                    kurzfristiger realisieren.
                                </p>
                            </details>
                        </div>
                    </section>

                    {/* CTA: local + link to main Studios page */}
                    <section className="systems-cta">
                        <h2 className="studio-color">Fotograf in München gesucht?
                            Business-Fotografie &amp; Videoproduktion</h2>
                        <p id="systems-cta-p">
                            Ob <strong>Eventfotograf in München</strong>, <strong>Business-Headshots</strong>,
                            <strong> Teamfotos</strong> oder <strong>Videoproduktion</strong> für Imagefilm, Recruiting
                            oder Social Media: Erzählen Sie uns kurz von Ihrem Projekt – wir melden uns mit einer
                            konkreten Idee, einem Vorschlag zum Vorgehen und transparenten Konditionen.
                        </p>
                        <div style={{display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center"}}>
                            <Link to="/" state={{scrollTo: 'contact-section'}}>
                                <button><p>Kontakt aufnehmen</p></button>
                            </Link>
                            <Link to="/studios">
                                <button><p>Mehr zu Dockline Studios</p></button>
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default FotografMuenchen;